<template>
  <article>
    <activity-stage
      :customRootContainerClass="'face-flip'"
      :customContentContainerStyles="{ height: 'calc(100% - 61px)' }"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content" class="face-flip-content" v-if="currentRound.steps">
        <ul ref="faceCards" class="top">
          <li
            v-for="(step, index) in currentRound?.steps"
            :key="step.answerImage?.image?.src"
            transition="kwtStagger"
            class="card-item"
          >
            <flippable-card :is-flipped="index === progress.stepIndex || showAllCards">
              <div slot="card-back" class="back-side">
                <img
                  alt="Back of card image"
                  :src="currentRound?.cardBack?.src ?? backOfCardPath"
                  style="max-width: 264px; max-height: 304px;"
                />
              </div>
              <div slot="card-front" class="front-side">
                <img
                  :alt="step.answerImage?.image?.description"
                  :src="step.answerImage?.image?.src"
                  style="max-width: 264px; max-height: 304px;"
                />
              </div>
            </flippable-card>
          </li>
        </ul>
        <div class="bottom">
          <div
            v-for="(step, index) in currentRound?.steps"
            :key="`text_${index}`"
            class="td-container"
          >
            <blinking-cursor-text
              v-if="index === progress.stepIndex"
              transition="slide-in"
              :text="step.answer"
              :text-step="index"
            ></blinking-cursor-text>
          </div>
          <div aria-live="assertive" role="region">
            <success-check-solid-icon
              height="26"
              width="37"
              v-if="showAllCards"
            ></success-check-solid-icon>
          </div>
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineMax } from 'gsap';

import backOfCardPath from 'images/face-flip-card-back.png';

import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import SuccessCheckSolidIcon from 'components/icons/SuccessCheckSolidIcon.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import FlippableCard from 'components/activity/FlippableCard.vue';
import kwtStagger from 'utils/transitions/StaggerTransition';

export default {
  name: 'FaceFlip',

  components: {
    ActivityStage,
    BlinkingCursorText,
    FlippableCard,
    SuccessCheckSolidIcon
  },

  transitions: {
    kwtStagger
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      showAllCards: false,
      backOfCardPath
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    async loadActivity() {
      this.createRounds();

      await this.loadImages();
    },

    async loadImages() {
      this.queueImage(backOfCardPath);

      this.rounds.forEach(async round => {
        round.steps.forEach(async step => {
          if (step.imageLocation) {
            this.queueImage(step.imageLocation);
          }
        });
      });

      return this.downloadImages();
    },

    startActivity() {
      this.$nextTick(this.setActivityContainerHeight);

      const timeline = new TimelineMax();

      timeline
        .call(this.setupNewRound, null, null, 0.5)
        .call(this.enableKeyboard)
        .call(this.errorLoggerStart);
    },

    staggerCardsIn({ children }, onComplete) {
      TweenMax.staggerFromTo(children, 0.3, { autoAlpha: 0 }, { autoAlpha: 1, onComplete }, 0.3);
    },

    slideTextIn(textEl, onComplete) {
      TweenMax.fromTo(
        textEl,
        1,
        { autoAlpha: 0, display: 'block', x: 100 },
        { autoAlpha: 1, x: 0, onComplete }
      );
    },

    nextStep(firstStep) {
      if (this.isEndOfRound) {
        this.finishRound();
        return;
      }

      this.setCurrentStep({ firstStep });
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.nextStep();
      }
    },

    finishRound() {
      if (this.isLastRound) {
        this.completeActivity();
      } else {
        this.setupNewRound();
      }
    },

    // ActivityManager Override
    completeActivity() {
      const finishTimeline = new TimelineMax({ delay: 1.25 });

      this.incrementStep();

      finishTimeline
        .call(
          () => {
            this.showAllCards = true;
          },
          null,
          null
        )
        .call(this.playCongrats, null, null, 3)
        .call(this.processEndOfActivity);
    }
  }
};
</script>

<style lang="scss">
.face-flip {
  height: 100%;
  width: 100%;

  .face-flip-content {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
  }
  .slide-in-transition {
    transition: all 1s ease;
  }

  .slide-in-enter {
    opacity: 0;
    transform: translateX(100px);
  }

  .slide-in-leave {
    opacity: 0;
  }

  .top {
    display: flex;
    justify-content: space-evenly;
    list-style: none;
    padding: 0px;
    min-width: 100%;
    min-height: 80%;
    height: max-content;
    width: max-content;

    .flippable-card {
      .card-back {
        height: 100%;
        width: 100%;
      }
    }

    & .front-side {
      position: relative;
      height: 100%;
      width: 100%;
      img {
        height: 100%;
        width: 100%;
      }
    }

    & .back-side {
      position: relative;
      height: 100%;
      width: 100%;
      img {
        height: 100%;
        width: 100%;
      }
    }
  }

  .bottom {
    width: 100%;
    height: 100%;
    text-align: center;
    font-size: 20px;
    font-family: hwtfutura;
  }
}

// .chrome-os,
// .tablet {
//   .face-flip {
//     .top {
//       margin: 0px 0px 10px 0px;

//       img {
//         max-height: 200px;
//       }
//     }
//   }
// }
</style>
